import React, { useState, useEffect, useCallback } from 'react';
import { Users, Mail, Calendar, FileText, History, CalendarDays, DollarSign, Clock, Film, RefreshCw } from 'lucide-react';
import {
  AdminLogin,
  StatsCards,
  SubscribersTab,
  InquiriesTab,
  ComposeTab,
  HistoryTab,
  CalendarTab,
  ScheduledTab,
  SalesTab,
  MediaTab,
  EventDetailModal,
  PreviewModal,
} from '../components/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'subscribers', label: 'Subscribers', icon: Users, activeClass: 'bg-teal-600 text-white', countKey: 'subscribers' },
  { id: 'inquiries', label: 'Inquiries', icon: Calendar, activeClass: 'bg-teal-600 text-white', countKey: 'inquiries' },
  { id: 'sales', label: 'Sales', icon: DollarSign, activeClass: 'bg-green-600 text-white' },
  { id: 'compose', label: 'Compose Newsletter', icon: FileText, activeClass: 'bg-gold-500 text-black' },
  { id: 'history', label: 'Send History', icon: History, activeClass: 'bg-teal-600 text-white' },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays, activeClass: 'bg-purple-600 text-white' },
  { id: 'scheduled', label: 'Scheduled', icon: Clock, activeClass: 'bg-purple-600 text-white' },
  { id: 'media', label: 'Media', icon: Film, activeClass: 'bg-teal-600 text-white' },
];

export default function AdminPage() {
  // Auth state
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Dashboard state
  const [activeTab, setActiveTab] = useState('subscribers');
  const [stats, setStats] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [newsletterHistory, setNewsletterHistory] = useState([]);

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Newsletter composer state
  const [newsletterSubject, setNewsletterSubject] = useState('');
  const [newsletterContent, setNewsletterContent] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const getAdminPassword = useCallback(() => {
    return sessionStorage.getItem('adminPassword') || password;
  }, [password]);

  const fetchData = useCallback(async () => {
    const savedPassword = getAdminPassword();
    const headers = {
      'Content-Type': 'application/json',
      'x-admin-password': savedPassword,
    };

    try {
      const [statsRes, subsRes, inqRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats`, { headers }),
        fetch(`${API_URL}/api/admin/subscribers`, { headers }),
        fetch(`${API_URL}/api/admin/inquiries`, { headers }),
        fetch(`${API_URL}/api/admin/newsletter/history`, { headers }),
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (subsRes.ok) setSubscribers(await subsRes.json());
      if (inqRes.ok) setInquiries(await inqRes.json());
      if (historyRes.ok) setNewsletterHistory(await historyRes.json());
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  }, [getAdminPassword]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        setIsLoggedIn(true);
        sessionStorage.setItem('adminPassword', password);
        fetchData();
      } else {
        setError('Invalid password');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const updateInquiryStatus = async (id, status) => {
    const savedPassword = getAdminPassword();
    try {
      await fetch(`${API_URL}/api/admin/inquiries/${id}/status?status=${status}`, {
        method: 'PATCH',
        headers: { 'x-admin-password': savedPassword },
      });
      fetchData();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const deleteSubscriber = async (email) => {
    if (!window.confirm(`Delete subscriber ${email}?`)) return;
    const savedPassword = getAdminPassword();
    try {
      await fetch(`${API_URL}/api/admin/subscribers/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers: { 'x-admin-password': savedPassword },
      });
      fetchData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const exportFile = (endpoint, filename) => {
    const savedPassword = getAdminPassword();
    fetch(`${API_URL}/api/admin/export/${endpoint}`, {
      headers: { 'x-admin-password': savedPassword },
    })
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      })
      .catch(err => console.error('Export failed:', err));
  };

  const previewNewsletter = async () => {
    const savedPassword = getAdminPassword();
    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': savedPassword,
        },
        body: JSON.stringify({
          subject: newsletterSubject,
          content: newsletterContent,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html);
        setShowPreview(true);
      }
    } catch (err) {
      console.error('Failed to preview:', err);
    }
  };

  const sendNewsletter = async () => {
    if (!newsletterSubject.trim() || !newsletterContent.trim()) {
      alert('Please enter both subject and content');
      return;
    }

    if (!window.confirm(`Send newsletter to ${stats?.active_subscribers || 0} subscribers?`)) {
      return;
    }

    setSending(true);
    setSendResult(null);
    const savedPassword = getAdminPassword();

    try {
      const response = await fetch(`${API_URL}/api/admin/newsletter/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-admin-password': savedPassword,
        },
        body: JSON.stringify({
          subject: newsletterSubject,
          content: newsletterContent,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSendResult({
          success: true,
          message: data.message,
          results: data.results,
        });
        setNewsletterSubject('');
        setNewsletterContent('');
        fetchData();
      } else {
        setSendResult({
          success: false,
          message: data.detail || 'Failed to send newsletter',
        });
      }
    } catch (err) {
      setSendResult({
        success: false,
        message: 'Connection error',
      });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const savedPassword = sessionStorage.getItem('adminPassword');
    if (savedPassword) {
      setPassword(savedPassword);
      setIsLoggedIn(true);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      fetchData();
    }
  }, [isLoggedIn, fetchData]);

  if (!isLoggedIn) {
    return (
      <AdminLogin
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        error={error}
        loading={loading}
        onSubmit={handleLogin}
      />
    );
  }

  const tabCounts = {
    subscribers: subscribers.length,
    inquiries: inquiries.length,
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gold-400">Admin Dashboard</h1>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>

        <StatsCards stats={stats} />

        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-6">
          {TABS.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            const count = tab.countKey ? tabCounts[tab.countKey] : null;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                  isActive ? tab.activeClass : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={18} />
                {tab.label}{count !== null ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'subscribers' && (
          <SubscribersTab
            subscribers={subscribers}
            onDelete={deleteSubscriber}
            onExport={() => exportFile('subscribers', 'subscribers')}
          />
        )}

        {activeTab === 'inquiries' && (
          <InquiriesTab
            inquiries={inquiries}
            onUpdateStatus={updateInquiryStatus}
            onExport={() => exportFile('bookings', 'bookings')}
          />
        )}

        {activeTab === 'sales' && (
          <SalesTab apiUrl={API_URL} password={getAdminPassword()} />
        )}

        {activeTab === 'compose' && (
          <ComposeTab
            activeSubscribers={stats?.active_subscribers || 0}
            subject={newsletterSubject}
            setSubject={setNewsletterSubject}
            content={newsletterContent}
            setContent={setNewsletterContent}
            sending={sending}
            sendResult={sendResult}
            onPreview={previewNewsletter}
            onSend={sendNewsletter}
          />
        )}

        {activeTab === 'history' && (
          <HistoryTab newsletterHistory={newsletterHistory} />
        )}

        {activeTab === 'calendar' && (
          <CalendarTab
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
            inquiries={inquiries}
            onSelectEvent={setSelectedEvent}
          />
        )}

        {activeTab === 'scheduled' && <ScheduledTab />}

        {activeTab === 'media' && <MediaTab />}

        {/* Modals */}
        {selectedEvent && (
          <EventDetailModal
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
          />
        )}

        {showPreview && (
          <PreviewModal
            previewHtml={previewHtml}
            onClose={() => setShowPreview(false)}
            onSend={sendNewsletter}
          />
        )}
      </div>
    </div>
  );
}
